/**
 * TAILWIND CHECKBOX KOMPONENTE
 *
 * Custom Checkbox mit Brand Gold Akzentfarbe. Kompatibel mit React Hook Form (forwardRef).
 *
 * Props (zusätzlich zu allen nativen input-Attributen):
 *   label?      - ReactNode, Label neben der Checkbox
 *   error?      - boolean (default: false), roter Fehlerzustand
 *   helperText? - string, Hilfstext unter der Checkbox
 *   checked     - boolean
 *   onChange     - ChangeEventHandler
 *   disabled?   - boolean
 *
 * Usage:
 *   <TWCheckbox checked={val} onChange={e => setVal(e.target.checked)} label="Option" />
 *   <TWCheckbox {...register('agb')} label="AGB akzeptieren" error={!!errors.agb} />
 */

import React, { forwardRef, useId } from 'react';
import clsx from 'clsx';

export interface TWCheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Label neben der Checkbox */
  label?: React.ReactNode;
  /** Fehlerzustand */
  error?: boolean;
  /** Hilfstext unter der Checkbox */
  helperText?: string;
}

export const TWCheckbox = forwardRef<HTMLInputElement, TWCheckboxProps>(
  ({ label, error = false, helperText, className, id, disabled, checked, ...props }, ref) => {
    const generatedId = useId();
    const checkboxId = id || `tw-checkbox-${generatedId}`;

    return (
      <div className="flex flex-col">
        <label
          htmlFor={checkboxId}
          className={clsx(
            'inline-flex items-center gap-2 cursor-pointer select-none',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          {/* Tailwind-Plus Checkbox Pattern: native input + SVG-Overlay im Grid.
              Mit 44x44 Touch-Target per ::before Pseudo-Element (WCAG 2.5.5 / Apple HIG). */}
          <div className="group relative grid size-4 grid-cols-1 shrink-0
            before:absolute before:-inset-3.5 before:content-['']">
            <input
              ref={ref}
              id={checkboxId}
              type="checkbox"
              checked={checked}
              disabled={disabled}
              className={clsx(
                'relative col-start-1 row-start-1 appearance-none rounded-sm border bg-white',
                // Unchecked Border
                error
                  ? 'border-red-500'
                  : 'border-gray-300 dark:border-white/10 dark:bg-white/5',
                // Checked / Indeterminate (Brand Gold)
                'checked:border-brand checked:bg-brand indeterminate:border-brand indeterminate:bg-brand',
                // Focus
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
                // Disabled
                'disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100',
                'dark:disabled:border-white/5 dark:disabled:bg-white/10 dark:disabled:checked:bg-white/10',
                'forced-colors:appearance-auto'
              )}
              {...props}
            />
            <svg
              fill="none"
              viewBox="0 0 14 14"
              className="pointer-events-none relative col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-gray-950 group-has-disabled:stroke-gray-950/25 dark:group-has-disabled:stroke-white/25"
            >
              <path
                d="M3 8L6 11L11 3.5"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="opacity-0 group-has-checked:opacity-100"
              />
              <path
                d="M3 7H11"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="opacity-0 group-has-indeterminate:opacity-100"
              />
            </svg>
          </div>

          {/* Label text */}
          {label && (
            <span
              className={clsx(
                'text-sm',
                error ? 'text-red-500' : 'text-gray-700 dark:text-gray-300'
              )}
            >
              {label}
            </span>
          )}
        </label>

        {/* Helper text */}
        {helperText && (
          <p
            className={clsx(
              'mt-1 text-xs ml-6',
              error ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'
            )}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

TWCheckbox.displayName = 'TWCheckbox';

export default TWCheckbox;
